import { useState, useCallback } from 'react';
import type { SortField, SortOrder } from '../types';

interface UseTableStateOptions {
  initialExpandedVendors?: string[];
}

interface UseTableStateReturn {
  // Expansion
  expandedVendors: Set<string>;
  toggleVendor: (vendorName: string) => void;
  isExpanded: (vendorName: string) => boolean;
  collapseAll: () => void;
  expandAll: (vendorNames: string[]) => void;

  // Sorting
  sortColumn: SortField | null;
  sortDirection: SortOrder;
  setSort: (column: SortField) => void;
}

export function useTableState(options?: UseTableStateOptions): UseTableStateReturn {
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(
    () => new Set(options?.initialExpandedVendors || [])
  );

  // Combined sort state to avoid nested state update issues
  const [sortState, setSortState] = useState<{ column: SortField | null; direction: SortOrder }>({
    column: null,
    direction: 'desc',
  });

  const toggleVendor = useCallback((vendorName: string) => {
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendorName)) {
        next.delete(vendorName);
      } else {
        next.add(vendorName);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (vendorName: string) => expandedVendors.has(vendorName),
    [expandedVendors]
  );

  const collapseAll = useCallback(() => {
    setExpandedVendors(new Set());
  }, []);

  const expandAll = useCallback((vendorNames: string[]) => {
    setExpandedVendors(new Set(vendorNames));
  }, []);

  const setSort = useCallback((column: SortField) => {
    setSortState((prev) => {
      if (prev.column === column) {
        // Same column - flip direction
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // New column - default to ascending
      return { column, direction: 'asc' };
    });
  }, []);

  return {
    expandedVendors,
    toggleVendor,
    isExpanded,
    collapseAll,
    expandAll,
    sortColumn: sortState.column,
    sortDirection: sortState.direction,
    setSort,
  };
}
